import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, signOut } from 'firebase/auth';
import { collection, doc, setDoc, deleteDoc, onSnapshot, writeBatch } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import {
  GstProfile,
  ExtractedGstInvoice,
  Gstr2bRecord,
  GstUploadMetadata,
  PrivacyShieldMode
} from '../types/gst';
import { GST_STATE_CODES } from '../services/mathValidator';
import {
  onAuthStateListener,
  resolveUserRole,
  AllowedUserRole,
  signInWithGoogle as authSignInWithGoogle,
  signInWithEmail as authSignInWithEmail,
  signUpWithEmail as authSignUpWithEmail,
  sendResetPassword as authSendResetPassword
} from '../firebase/auth';

interface GstContextType {
  user: User | null;
  clientId: string;
  clientLegalName: string;
  userRole: AllowedUserRole;
  isAuthorized: boolean;
  authLoading: boolean;
  isSuperOrCa: boolean;
  gstProfiles: GstProfile[];
  activeGstin: string;
  financialYear: string;
  taxPeriod: string;
  invoices: ExtractedGstInvoice[];
  gstr2bRecords: Gstr2bRecord[];
  uploads: GstUploadMetadata[];
  isLoading: boolean;
  showProfileModal: boolean;
  copilotOpen: boolean;
  securityModalOpen: boolean;
  purgeModalOpen: boolean;
  legalGateOpen: boolean;
  privacyShieldMode: PrivacyShieldMode;
  themeMode: 'dark' | 'light';
  setCopilotOpen: (open: boolean) => void;
  setShowProfileModal: (show: boolean) => void;
  setSecurityModalOpen: (open: boolean) => void;
  setPurgeModalOpen: (open: boolean) => void;
  setLegalGateOpen: (open: boolean) => void;
  setPrivacyShieldMode: (mode: PrivacyShieldMode) => void;
  toggleTheme: () => void;
  setActiveGstin: (gstin: string) => void;
  setFinancialYear: (fy: string) => void;
  setTaxPeriod: (period: string) => void;
  saveGstProfile: (profile: Partial<GstProfile>) => Promise<void>;
  updateInvoice: (updated: ExtractedGstInvoice, actionNote?: string) => Promise<void>;
  purgePeriodData: (fy: string, period: string) => Promise<{ filesDeleted: number; recordsShredded: number }>;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, pass: string) => Promise<void>;
  signUpWithEmail: (email: string, pass: string, name?: string, company?: string) => Promise<void>;
  sendResetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshData: () => Promise<void>;
}

const GstContext = createContext<GstContextType | undefined>(undefined);

export const FINANCIAL_YEARS = ["2026-27", "2025-26", "2024-25"];
export const TAX_PERIODS = [
  "April", "May", "June", "July", "August", "September",
  "October", "November", "December", "January", "February", "March", "Annual"
];

export const GstProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [clientId, setClientId] = useState<string>("");
  const [clientLegalName, setClientLegalName] = useState<string>("Client Organization");
  const [userRole, setUserRole] = useState<AllowedUserRole>("PUBLIC");
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [isSuperOrCa, setIsSuperOrCa] = useState<boolean>(false);

  const [gstProfiles, setGstProfiles] = useState<GstProfile[]>([]);
  const [activeGstin, setActiveGstin] = useState<string>("");
  const [financialYear, setFinancialYear] = useState<string>("2025-26");
  const [taxPeriod, setTaxPeriod] = useState<string>("July");

  const [invoices, setInvoices] = useState<ExtractedGstInvoice[]>([]);
  const [gstr2bRecords, setGstr2bRecords] = useState<Gstr2bRecord[]>([]);
  const [uploads, setUploads] = useState<GstUploadMetadata[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [copilotOpen, setCopilotOpen] = useState<boolean>(false);

  // Security & Data Protection Controls
  const [securityModalOpen, setSecurityModalOpen] = useState<boolean>(false);
  const [purgeModalOpen, setPurgeModalOpen] = useState<boolean>(false);
  const [legalGateOpen, setLegalGateOpen] = useState<boolean>(false);
  const [privacyShieldMode, setPrivacyShieldMode] = useState<PrivacyShieldMode>('STANDARD');

  // Theme Mode (Dark / Light toggle)
  const [themeMode, setThemeMode] = useState<'dark' | 'light'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('gst_theme_mode');
      if (saved === 'light' || saved === 'dark') return saved;
    }
    return 'dark';
  });

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', themeMode);
      document.documentElement.classList.toggle('light', themeMode === 'light');
      document.documentElement.classList.toggle('dark', themeMode === 'dark');
      localStorage.setItem('gst_theme_mode', themeMode);
    }
  }, [themeMode]);

  const toggleTheme = () => {
    setThemeMode(prev => (prev === 'dark' ? 'light' : 'dark'));
  };

  // Read URL query params for Admin/CA cross-portal navigation: ?clientId=...&fy=...&period=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paramClientId = params.get('clientId');
    const paramFy = params.get('fy');
    const paramPeriod = params.get('period');
    const paramGstin = params.get('gstin');

    if (paramClientId) setClientId(paramClientId);
    if (paramFy && FINANCIAL_YEARS.includes(paramFy)) setFinancialYear(paramFy);
    if (paramPeriod && TAX_PERIODS.includes(paramPeriod)) setTaxPeriod(paramPeriod);
    if (paramGstin) setActiveGstin(paramGstin);
  }, []);

  // Listen to Auth State and Resolve Strict Role
  useEffect(() => {
    setAuthLoading(true);
    const unsub = onAuthStateListener(async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const authInfo = await resolveUserRole(currentUser);
          setUserRole(authInfo.role);
          setIsAuthorized(authInfo.isAuthorized);
          setIsSuperOrCa(authInfo.role === "ADMIN");

          // Determine target clientId
          const params = new URLSearchParams(window.location.search);
          const paramClientId = params.get('clientId');
          // Admins can switch client context via URL query; CLIENT users are locked to their assigned clientId
          let resolvedId = "";
          if (authInfo.role === "ADMIN" && paramClientId) {
            resolvedId = paramClientId;
          } else {
            resolvedId = authInfo.clientId || currentUser.uid;
          }
          setClientId(resolvedId);

          if (authInfo.companyName) {
            setClientLegalName(authInfo.companyName);
          } else {
            setClientLegalName(currentUser.displayName || currentUser.email || "Johalibash & Company");
          }

          // Check if legal DPA consent has been recorded for this client
          if (resolvedId) {
            const consentStored = localStorage.getItem(`gst_consent_${resolvedId}`);
            if (!consentStored) {
              setLegalGateOpen(true);
            }
          }
        } catch (err) {
          console.error("Failed to resolve user role:", err);
          setUserRole("PUBLIC");
          setIsAuthorized(false);
        } finally {
          setAuthLoading(false);
          setIsLoading(false);
        }
      } else {
        setUserRole("PUBLIC");
        setIsAuthorized(false);
        setAuthLoading(false);
        setIsLoading(false);
      }
    });

    return () => unsub();
  }, []);

  // Listen to GST Profiles under /clients/{clientId}/gst_profile (ONLY IF AUTHORIZED)
  useEffect(() => {
    if (!clientId || !isAuthorized) {
      setGstProfiles([]);
      return;
    }

    const profilesRef = collection(db, 'clients', clientId, 'gst_profile');
    const unsub = onSnapshot(profilesRef, (snapshot) => {
      const list: GstProfile[] = [];
      snapshot.forEach(docSnap => {
        list.push(docSnap.data() as GstProfile);
      });
      setGstProfiles(list);

      if (list.length === 0) {
        setShowProfileModal(true);
      } else if (!activeGstin) {
        setActiveGstin(list[0].gstin);
      }
      setIsLoading(false);
    }, (err) => {
      console.warn("Snapshot error on gst_profile (fallback profile if newly initialized):", err);
      const defaultProfile: GstProfile = {
        gstin: "27AABCU9603R1ZM",
        clientId,
        entityType: "PVT_LTD",
        legalName: clientLegalName || "Client Enterprise",
        tradeName: "",
        pan: "AABCU9603R",
        cinOrLlpin: "U72900MH2024PTC428901",
        stateCode: "27",
        stateName: "Maharashtra",
        filingFrequency: "MONTHLY",
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setGstProfiles([defaultProfile]);
      setActiveGstin(defaultProfile.gstin);
      setIsLoading(false);
    });

    return () => unsub();
  }, [clientId, clientLegalName, isAuthorized]);

  // Listen to Invoices, 2B records, and Uploads for active client, FY, and Period (ONLY IF AUTHORIZED)
  useEffect(() => {
    if (!clientId || !isAuthorized) {
      setInvoices([]);
      setGstr2bRecords([]);
      setUploads([]);
      return;
    }

    // Invoices listener
    const invRef = collection(db, 'clients', clientId, 'gst_invoices');
    const unsubInv = onSnapshot(invRef, (snapshot) => {
      const list: ExtractedGstInvoice[] = [];
      snapshot.forEach(d => {
        const data = d.data() as ExtractedGstInvoice;
        if (taxPeriod === 'Annual' || data.taxPeriod === taxPeriod) {
          if (!financialYear || data.financialYear === financialYear) {
            list.push(data);
          }
        }
      });
      setInvoices(list);
    }, (err) => console.warn("Invoices listener:", err));

    // 2B records listener
    const recRef = collection(db, 'clients', clientId, 'gst_2b_records');
    const unsub2b = onSnapshot(recRef, (snapshot) => {
      const list: Gstr2bRecord[] = [];
      snapshot.forEach(d => {
        const data = d.data() as Gstr2bRecord;
        if (taxPeriod === 'Annual' || data.taxPeriod === taxPeriod) {
          if (!financialYear || data.financialYear === financialYear) {
            list.push(data);
          }
        }
      });
      setGstr2bRecords(list);
    }, (err) => console.warn("2B records listener:", err));

    // Uploads listener
    const upRef = collection(db, 'clients', clientId, 'gst_uploads');
    const unsubUp = onSnapshot(upRef, (snapshot) => {
      const list: GstUploadMetadata[] = [];
      snapshot.forEach(d => {
        const data = d.data() as GstUploadMetadata;
        if (taxPeriod === 'Annual' || data.taxPeriod === taxPeriod) {
          if (!financialYear || data.financialYear === financialYear) {
            list.push(data);
          }
        }
      });
      setUploads(list);
    }, (err) => console.warn("Uploads listener:", err));

    return () => {
      unsubInv();
      unsub2b();
      unsubUp();
    };
  }, [clientId, financialYear, taxPeriod, isAuthorized]);

  const saveGstProfile = async (profileData: Partial<GstProfile>) => {
    if (!clientId) throw new Error("No client authenticated");
    const gstin = (profileData.gstin || "").trim().toUpperCase();
    if (!gstin) throw new Error("GSTIN is required");

    const stateCode = gstin.substring(0, 2);
    const fullProfile: GstProfile = {
      gstin,
      clientId,
      entityType: profileData.entityType || 'PVT_LTD',
      legalName: profileData.legalName || clientLegalName || "Registered Entity",
      tradeName: profileData.tradeName || "",
      pan: gstin.substring(2, 12),
      cinOrLlpin: profileData.cinOrLlpin || "",
      stateCode,
      stateName: GST_STATE_CODES[stateCode] || "Other Territory",
      filingFrequency: profileData.filingFrequency || 'MONTHLY',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const docRef = doc(db, 'clients', clientId, 'gst_profile', gstin);
    await setDoc(docRef, fullProfile, { merge: true });
    setActiveGstin(gstin);
    setShowProfileModal(false);
  };

  const updateInvoice = async (updated: ExtractedGstInvoice, actionNote?: string) => {
    if (!clientId) return;
    const invRef = doc(db, 'clients', clientId, 'gst_invoices', updated.id);
    const auditEntry = {
      action: actionNote || 'MANUAL_EDIT',
      timestamp: new Date().toISOString(),
      userId: user?.uid || clientId,
      details: actionNote || 'Manual ledger modification'
    };

    const newInvoice = {
      ...updated,
      audit_trail: [...(updated.audit_trail || []), auditEntry]
    };

    await setDoc(invRef, newInvoice, { merge: true });
  };

  const purgePeriodData = async (fy: string, period: string) => {
    if (!clientId) throw new Error("No authenticated tenant");

    const batch = writeBatch(db);
    let filesDeleted = 0;
    let recordsShredded = 0;

    // 1. Delete matching invoices
    const matchingInvoices = invoices.filter(
      i => i.financialYear === fy && (period === 'Annual' || i.taxPeriod === period)
    );
    matchingInvoices.forEach(inv => {
      const invRef = doc(db, 'clients', clientId, 'gst_invoices', inv.id);
      batch.delete(invRef);
      recordsShredded++;
    });

    // 2. Delete matching 2B records
    const matching2b = gstr2bRecords.filter(
      r => r.financialYear === fy && (period === 'Annual' || r.taxPeriod === period)
    );
    matching2b.forEach(rec => {
      const recRef = doc(db, 'clients', clientId, 'gst_2b_records', rec.id);
      batch.delete(recRef);
      recordsShredded++;
    });

    // 3. Delete matching uploads
    const matchingUploads = uploads.filter(
      u => u.financialYear === fy && (period === 'Annual' || u.taxPeriod === period)
    );
    matchingUploads.forEach(up => {
      const upRef = doc(db, 'clients', clientId, 'gst_uploads', up.id);
      batch.delete(upRef);
      filesDeleted++;
    });

    // 4. Record audit entry
    const auditRef = doc(collection(db, 'clients', clientId, 'audit_trail'));
    batch.set(auditRef, {
      action: 'DATA_PURGED_CRYPTOGRAPHICALLY',
      financialYear: fy,
      taxPeriod: period,
      filesDeleted,
      recordsShredded,
      purgedBy: user?.email || 'Authorized Client Executive',
      timestamp: new Date().toISOString(),
      details: `Client purged data for ${period} ${fy}. Certificate of Data Destruction issued.`
    });

    await batch.commit();
    await refreshData();
    return { filesDeleted, recordsShredded };
  };

  const signInWithGoogle = async () => {
    await authSignInWithGoogle();
  };

  const signInWithEmail = async (email: string, pass: string) => {
    await authSignInWithEmail(email, pass);
  };

  const signUpWithEmail = async (email: string, pass: string, name?: string, company?: string) => {
    await authSignUpWithEmail(email, pass, name, company);
  };

  const sendResetPassword = async (email: string) => {
    await authSendResetPassword(email);
  };

  const logout = async () => {
    await signOut(auth);
  };

  const refreshData = async () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 300);
  };

  return (
    <GstContext.Provider
      value={{
        user,
        clientId,
        clientLegalName,
        userRole,
        isAuthorized,
        authLoading,
        isSuperOrCa,
        gstProfiles,
        activeGstin,
        financialYear,
        taxPeriod,
        invoices,
        gstr2bRecords,
        uploads,
        isLoading,
        showProfileModal,
        copilotOpen,
        securityModalOpen,
        purgeModalOpen,
        legalGateOpen,
        privacyShieldMode,
        themeMode,
        setCopilotOpen,
        setShowProfileModal,
        setSecurityModalOpen,
        setPurgeModalOpen,
        setLegalGateOpen,
        setPrivacyShieldMode,
        toggleTheme,
        setActiveGstin,
        setFinancialYear,
        setTaxPeriod,
        saveGstProfile,
        updateInvoice,
        purgePeriodData,
        signInWithGoogle,
        signInWithEmail,
        signUpWithEmail,
        sendResetPassword,
        logout,
        refreshData
      }}
    >
      {children}
    </GstContext.Provider>
  );
};

export const useGst = () => {
  const context = useContext(GstContext);
  if (!context) {
    throw new Error("useGst must be used within a GstProvider");
  }
  return context;
};
